import { CHAPTER_LIST_ORDER_PREFIX } from '@/shared/lib/storageKeys'
/**
 * Die Reihenfolge, in der eine Liste ihre Kapitel gerade zeigt - nach Filterung und
 * Gruppierung.
 *
 * Die Detailseite blättert damit durch genau das, was auf der Liste stand. Sie könnte
 * die Sortierung auch selbst nachbauen, müsste dafür aber Gruppierung und Filter
 * kennen - und wich beim ersten Unterschied wieder ab: Vor der Einführung sprangen die
 * Pfeile auf Kapitel, die die Liste gar nicht zeigte.
 *
 * In der `sessionStorage`, damit ein Neuladen der Detailseite die Pfeile nicht
 * abschaltet. Der Wert wird bei jedem Rendern der Liste neu geschrieben.
 */
export function rememberListOrder(key: string, slugs: string[]): void {
  try {
    sessionStorage.setItem(CHAPTER_LIST_ORDER_PREFIX + key, JSON.stringify(slugs))
  } catch {
    // Privater Modus o.ä. - dann gibt es eben keine Pfeile nach dem Neuladen.
  }
}

export function getListOrder(key: string): string[] {
  try {
    const raw = sessionStorage.getItem(CHAPTER_LIST_ORDER_PREFIX + key)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : []
  } catch {
    return []
  }
}

/**
 * Dieselbe Liste, aber auf Artikelebene: "Meine Titel" zeigt ein langes Kapitel als
 * mehrere Zeilen, eine je Artikel. Die Leseansicht blättert damit durch genau diese
 * Zeilen - auch über Kapitelgrenzen hinweg.
 *
 * Kurzkapitel haben nur einen Artikel; steht dessen Slug noch nicht fest, bleibt er
 * leer und die Leseansicht nimmt den ersten Artikel des Kapitels.
 */
export type ReadingListEntry = { chapterSlug: string; articleSlug: string | null }

const SEPARATOR = '\u0000'

export function rememberReadingList(key: string, entries: ReadingListEntry[]): void {
  rememberListOrder(
    `${key}:articles`,
    entries.map((entry) => `${entry.chapterSlug}${SEPARATOR}${entry.articleSlug ?? ''}`)
  )
}

export function getReadingList(key: string): ReadingListEntry[] {
  return getListOrder(`${key}:articles`).map((raw) => {
    const [chapterSlug, articleSlug = ''] = raw.split(SEPARATOR)
    return { chapterSlug, articleSlug: articleSlug || null }
  })
}

/** Position eines Artikels in der Liste, oder -1. Der Kapitelslug allein genügt für Kurzkapitel. */
export function readingListIndex(
  entries: ReadingListEntry[],
  chapterSlug: string | null,
  articleSlug: string | null
): number {
  if (!chapterSlug) return -1
  const exact = entries.findIndex(
    (entry) => entry.chapterSlug === chapterSlug && entry.articleSlug === articleSlug
  )
  if (exact >= 0) return exact
  return entries.findIndex((entry) => entry.chapterSlug === chapterSlug)
}
